import { useEffect, useRef } from "react"
import { useForm } from "react-hook-form"
import { useMutation } from "react-query"

import { createProduct, updateProduct } from "src/api/ManageItems"

import { useTracker } from "src/hooks/use_tracker"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import {
  availabilitiesValue,
  defaultRecurrenceValue,
  defaultValuesForProduct,
  defaultValuesForType,
  dollarsToPrecisionForType,
  withTaxableDefault,
} from "../Form/helpers"
import { MANAGE_LAUNCH_ADDON_AVAILABILITY_TOGGLED } from "../amplitude_events"

const DEFAULT_MARINA_TAXES = []

// ProductAvailability::LAUNCH_ADDONS — the key the availability checkboxes are
// registered under, and what DryStack::LaunchAddonProductsQuery filters on.
const LAUNCH_ADDONS = "launch_addons"

const useItemForm = ({
  product,
  availabilityOptions,
  productCategories,
  marinaTaxes = DEFAULT_MARINA_TAXES,
}) => {
  const marinaSlug = getCurrentMarinaSlug()
  const tracker = useTracker()
  const urlParams = new URLSearchParams(window.location.search)
  const effectiveType =
    urlParams.get("type") === "cancellationFee" ? "fee" : "standard"

  // Whether this save actually changed the launch add-on setting, held from
  // submit until the save comes back. Launch add-ons is one checkbox among
  // several in a form that saves everything at once, so there is no toggle to
  // hang an event on — only a before and an after. Firing on the checkbox
  // itself would count forms that were abandoned.
  const launchAddonChange = useRef(null)

  const onSubmit = (formData) => {
    const wasEnabled = Boolean(defaultValues.availabilities?.[LAUNCH_ADDONS])
    const nowEnabled = Boolean(formData.availabilities?.[LAUNCH_ADDONS])

    launchAddonChange.current =
      wasEnabled === nowEnabled
        ? null
        : {
            item_name: formData.name,
            item_type: formData.type,
            enabled: nowEnabled,
          }

    // Convert applied tax IDs to excluded tax IDs
    const allTaxIds = marinaTaxes.map((tax) => tax.id)
    const appliedTaxIds = formData.appliedTaxIds || []
    const excludedTaxIds = allTaxIds.filter((id) => !appliedTaxIds.includes(id))

    const data = {
      product_type: formData.type,
      product_category_id: formData.category,
      name: formData.name,
      sku: formData.sku,
      barcode: formData.barcode,
      space_type: formData.spaceType,
      price_per_unit: dollarsToPrecisionForType({
        type: formData.type,
        dollars: formData.defaultPrice,
      }),
      tax_rate: formData.defaultTax && formData.defaultTax / 100,
      default_recurrence: defaultRecurrenceValue(formData.defaultRecurrence),
      default_pricing_structure: formData.defaultPriceType,
      availabilities: availabilitiesValue(formData.availabilities),
      electric_type: formData.electricType,
      is_inventory: formData.isInventory,
      low_quantity_balance_threshold: formData.lowQuantityBalanceThreshold,
      inventory_product_id: formData.inventoryProductId,
      excluded_marina_tax_ids: excludedTaxIds,
      product_taxes_attributes: formData.productTaxesAttributes,
      taxable: formData.taxable,
    }

    const mutation = product ? updateMutation : createMutation
    mutation.mutate(data)
  }

  // Fired on the save rather than on the click, so it records a change that
  // actually landed. `enabled` distinguishes adoption from withdrawal; without
  // it the event cannot tell the two apart.
  const trackLaunchAddonAvailability = (response) => {
    if (!launchAddonChange.current) return

    tracker.trackEvent(MANAGE_LAUNCH_ADDON_AVAILABILITY_TOGGLED, {
      product_id: product?.id ?? response?.product?.id ?? null,
      // Product#category, the same fixed enum every other launch add-on event
      // carries. The form field holds a ProductCategory id, which is a separate
      // and marina-renamable thing — reading that would put two meanings under
      // one property. The enum is derived server-side, so on create it arrives
      // on the response rather than out of the form.
      category: product?.category ?? response?.product?.category ?? null,
      ...launchAddonChange.current,
    })

    launchAddonChange.current = null
  }

  const createMutation = useMutation(
    (data) => createProduct({ marinaSlug, data }),
    {
      onError: ({ message }) => {
        methods.setError("root.error", {
          message,
        })
      },
      onSuccess: (response) => {
        trackLaunchAddonAvailability(response)
        window.location.assign(`/manage/${marinaSlug}/items`)
      },
    }
  )

  const updateMutation = useMutation(
    (data) => updateProduct({ marinaSlug, productId: product.id, data }),
    {
      onError: ({ message }) => {
        methods.setError("root.error", {
          message,
        })
      },
      onSuccess: (response) => {
        trackLaunchAddonAvailability(response)
        window.location.assign(`/manage/${marinaSlug}/items`)
      },
    }
  )

  const defaultValues = withTaxableDefault({
    defaultValues: product
      ? defaultValuesForProduct({ product, availabilityOptions, marinaTaxes })
      : defaultValuesForType({
          type: effectiveType,
          availabilityOptions,
          productCategories,
          marinaTaxes,
        }),
    product,
  })

  const methods = useForm({ defaultValues, mode: "onBlur" })

  const type = methods.watch("type")
  const electricType = methods.watch("electricType")

  useEffect(() => {
    if (product) return

    // handle resetting default values when type changes
    const defaultValues = withTaxableDefault({
      defaultValues: defaultValuesForType({
        type,
        electricType,
        availabilityOptions,
        productCategories,
        marinaTaxes,
      }),
    })
    methods.reset(defaultValues)
  }, [
    type,
    electricType,
    availabilityOptions,
    productCategories,
    marinaTaxes,
    methods,
  ])

  return {
    formTitle: product ? "Edit item" : "New item",
    isEdit: !!product,
    isLoading: product ? updateMutation.isLoading : createMutation.isLoading,
    isSuccess: product ? updateMutation.isSuccess : createMutation.isSuccess,
    methods,
    onSubmit,
    marinaSlug,
  }
}

export default useItemForm
